import React, { useState, useEffect } from "react";
import {
  FileText,
  Upload,
  Search,
  Filter,
  Download,
  Eye,
  Folder,
  Clock,
  BarChart3,
  TrendingUp,
  Users,
  RefreshCw,
  AlertCircle,
  Calendar,
  Tag,
  User,
} from "lucide-react";
import Topbar from "../../components/TopbarNav";        
import HOSidebar from "../../components/MasterSideBar"; 
import UploadDocumentModal from "../../components/UploadDocumentModal";
import { motion } from "framer-motion";
import { getAllUserDocuments } from "../../api/documentApi";

export default function HODocumentation() {
  // 🔥 BYPASS AuthContext - Read directly from localStorage
  const [user, setUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sidebarVisible, setSidebarVisible] = useState(false);

  // 🔥 READ USER FROM localStorage DIRECTLY
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          return parsedUser;
        }
      } catch (error) {
        console.error("❌ Error parsing user from localStorage:", error);
      }
      return null;
    };

    // Load user on mount
    loadUser();

    // 🔥 LISTEN for localStorage changes
    const handleStorageChange = () => {
      const newUser = loadUser();
      if (newUser) {
        // Clear documents when user changes
        setDocuments([]);
        setFilterCategory("All");
        setFilterType("All");
        setSearchQuery("");
        setError(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // 🔥 CUSTOM event for same-tab changes
    const handleUserChange = () => {
      handleStorageChange();
    };
    window.addEventListener('userChanged', handleUserChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userChanged', handleUserChange);
    };
  }, []);

  const staffId = user?.id || null;

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  // Fetch documents
  const fetchAllUserDocuments = async () => {
    if (!staffId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const allDocuments = await getAllUserDocuments(staffId);
      setDocuments(allDocuments || []);
    } catch (err) {
      setError(`Failed to load documents: ${err.message}`);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔥 FETCH DOCUMENTS when user changes
  useEffect(() => {
    if (staffId && user) {
      fetchAllUserDocuments();
    }
  }, [staffId, user?.name]); // Trigger when user changes

  const handleUploadSuccess = async () => {
    await fetchAllUserDocuments();
    setIsModalOpen(false);
  };

  const handleRefresh = () => {
    fetchAllUserDocuments();
  };

  // Filtering logic
  const getFilteredDocuments = () => {
    let filtered = documents.filter((doc) => {
      const matchesCategory =
        filterCategory === "All" || doc.category === filterCategory;

      const matchesType =
        filterType === "All" ||
        (filterType === "owned" && doc.isOwned) ||
        (filterType === "assigned" && doc.isAssigned) ||
        (filterType === "shared" && doc.isShared);

      const matchesSearch =
        doc.originalName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesCategory && matchesType && matchesSearch;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "name":
          aVal = a.originalName?.toLowerCase() || "";
          bVal = b.originalName?.toLowerCase() || "";
          break;
        case "category":
          aVal = a.category?.toLowerCase() || "";
          bVal = b.category?.toLowerCase() || "";
          break;
        case "date":
        default:
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
          break;
      }

      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredDocuments = getFilteredDocuments();

  // Category stats
  const getCategoryStats = () => {
    const stats = documents.reduce((acc, doc) => {
      const category = doc.category || "Uncategorized";
      if (!acc[category]) {
        acc[category] = { total: 0, owned: 0, assigned: 0, shared: 0 };
      }
      acc[category].total++;
      if (doc.isOwned) acc[category].owned++;
      if (doc.isAssigned) acc[category].assigned++;
      if (doc.isShared) acc[category].shared++;
      return acc;
    }, {});

    return stats;
  };

  const categoryStats = getCategoryStats();

  const categories = [
    {
      name: "All",
      icon: Folder,
      count: documents.length,
      color: "bg-blue-600",
      description: "All documents",
    },
    {
      name: "Salary Documents",
      icon: BarChart3,
      count: categoryStats["Salary Documents"]?.total || 0,
      color: "bg-red-600",
      description: "Payroll & compensation",
    },
    {
      name: "Agreements & Contracts",
      icon: FileText,
      count: categoryStats["Agreements & Contracts"]?.total || 0,
      color: "bg-green-600",
      description: "Legal documents",
    },
    {
      name: "Audit Reports",
      icon: TrendingUp,
      count: categoryStats["Audit Reports"]?.total || 0,
      color: "bg-yellow-600",
      description: "Financial audits",
    },
  ];

  const getFileIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case "pdf":
        return <FileText className="text-red-600" size={18} />;
      case "doc":
      case "docx":
        return <FileText className="text-blue-600" size={18} />;
      case "xlsx":
      case "xls":
        return <FileText className="text-green-600" size={18} />;
      default:
        return <FileText className="text-gray-600" size={18} />;
    }
  };

  const getSourceBadge = (doc) => {
    if (doc.isOwned) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <User size={12} className="mr-1" />
          Owned
        </span>
      );
    } else if (doc.isAssigned) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Tag size={12} className="mr-1" />
          Assigned
        </span>
      );
    } else if (doc.isShared) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Users size={12} className="mr-1" />
          Shared
        </span>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"
        />
        <span className="ml-3 text-gray-600">Loading {user?.name}'s documents...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-responsive sidebar */}
      <HOSidebar 
        visible={sidebarVisible} 
        onClose={() => setSidebarVisible(false)}
        onLogout={handleLogout}
      />
      
      {/* Main content area */}
      <div className="md:ml-64">
        <Topbar onSidebarToggle={() => setSidebarVisible(true)} />

        {/* Main content with proper spacing */}
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8 space-y-6 md:space-y-8">
            
            {/* Header Section */}
            <div className="text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">
                Document Manager - {user?.name}
              </h1>
              <p className="text-base md:text-lg text-gray-600">
                Manage all documents - owned, assigned, and shared with you
              </p>
            </div>

            {/* Search and Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between bg-white p-4 md:p-6 rounded-xl shadow-sm">
              {/* Search Bar */}
              <div className="relative flex-grow max-w-lg">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-4 py-2 rounded-lg shadow transition disabled:opacity-50 flex items-center justify-center"
                  title="Refresh Documents"
                >
                  <RefreshCw size={18} className={isLoading ? "animate-spin mr-2" : "mr-2"} />
                  Refresh
                </button>

                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2 rounded-lg shadow transition flex items-center justify-center"
                >
                  <Upload className="mr-2 h-5 w-5" />
                  Upload Document
                </button>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-red-700 font-medium">{error}</p>
                  <p className="text-red-600 text-sm mt-1">
                    Try refreshing or check your internet connection.
                  </p>
                </div>
              </div>
            )}

            {/* Category Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 md:gap-6">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <motion.div
                    key={cat.name}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setFilterCategory(cat.name)}
                    className={`cursor-pointer bg-white rounded-xl p-4 md:p-6 shadow-md hover:shadow-lg transition-all ${
                      filterCategory === cat.name
                        ? "ring-2 ring-orange-500 shadow-lg"
                        : "ring-1 ring-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <div
                        className={`w-8 h-8 md:w-12 md:h-12 flex items-center justify-center rounded-lg ${cat.color}`}
                      >
                        <Icon className="text-white" size={16} />
                      </div>
                      <span className="text-lg md:text-2xl font-bold text-gray-900">
                        {cat.count}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm md:text-base truncate">
                      {cat.name}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 line-clamp-2">
                      {cat.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Filter size={20} />
                Filter & Sort Options
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                    <option value="All">All Categories</option>
                    {Object.keys(categoryStats).map((category) => (
                      <option key={category} value={category}>
                        {category} ({categoryStats[category].total})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Type
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                    <option value="All">All Types</option>
                    <option value="owned">Owned by Me</option>
                    <option value="assigned">Assigned to Me</option>
                    <option value="shared">Shared with Me</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                    <option value="date">Date</option>
                    <option value="name">Name</option>
                    <option value="category">Category</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order
                  </label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Documents Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Clock size={20} />
                  {user?.name}'s Documents
                  <span className="ml-2 inline-block rounded-full bg-orange-100 text-orange-800 px-3 py-1 text-sm font-semibold">
                    {filteredDocuments.length}
                  </span>
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold">Document</th>
                      <th className="text-left py-4 px-6 font-semibold">Category</th>
                      <th className="text-left py-4 px-6 font-semibold">Type</th>
                      <th className="text-left py-4 px-6 font-semibold">Status</th>
                      <th className="text-left py-4 px-6 font-semibold">Date</th>
                      <th className="text-left py-4 px-6 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredDocuments.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12">
                          <div className="flex flex-col items-center justify-center">
                            <FileText size={48} className="text-gray-400 mb-4" />
                            <p className="text-gray-500 text-lg mb-2">
                              No documents found for {user?.name}
                            </p>
                            <p className="text-gray-400 text-sm">
                              {documents.length === 0
                                ? "Upload some documents to get started"
                                : "Try adjusting your filters"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredDocuments.map((doc, index) => {
                        const dateObj = new Date(doc.createdAt);
                        const dateString = dateObj.toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        });

                        return (
                          <motion.tr
                            key={doc._id || doc.id || `doc-${index}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="hover:bg-orange-25 transition-colors"
                          >
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                {getFileIcon(doc.fileType)}
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {doc.originalName || "Unknown"}
                                  </p>
                                  {doc.description && (
                                    <p className="text-sm text-gray-500 truncate max-w-xs">
                                      {doc.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {doc.category || "Uncategorized"}
                              </span>
                            </td>
                            <td className="py-4 px-6">
                              <span className="text-sm font-medium text-gray-600">
                                {doc.fileType?.toUpperCase() || "Unknown"}
                              </span>
                            </td>
                            <td className="py-4 px-6">{getSourceBadge(doc)}</td>
                            <td className="py-4 px-6 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <Calendar size={14} />
                                {dateString}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <a
                                  href={doc.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded hover:bg-blue-50"
                                  title="View Document"
                                >
                                  <Eye size={18} />
                                </a>
                                <a
                                  href={doc.fileUrl}
                                  download={doc.originalName}
                                  className="text-green-600 hover:text-green-800 transition-colors p-1 rounded hover:bg-green-50"
                                  title="Download Document"
                                >
                                  <Download size={18} />
                                </a>
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>

        {/* Upload Modal */}
        <UploadDocumentModal
          key={`${staffId}-${user?.name}`} // Force new instance when user changes
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          staffId={staffId}
          onUploadSuccess={handleUploadSuccess}
        />
      </div>
    </div>
  );
}
